import { useEffect, useRef, useState, useCallback } from 'react';
import { TestProgressUpdate, APICallLog } from '../services/testExecutionService';

interface UseTestExecutionWebSocketProps {
  executionId: string | null;
  onProgressUpdate?: (update: TestProgressUpdate) => void;
  onApiCall?: (apiCall: APICallLog) => void;
  onComplete?: (results: any) => void;
  onError?: (error: string) => void;
}

interface WebSocketState {
  connected: boolean;
  error: string | null;
  reconnectAttempts: number;
}

export const useTestExecutionWebSocket = ({
  executionId,
  onProgressUpdate,
  onApiCall,
  onComplete,
  onError
}: UseTestExecutionWebSocketProps) => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [wsState, setWsState] = useState<WebSocketState>({
    connected: false,
    error: null,
    reconnectAttempts: 0
  });

  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;

  const connectRef = useRef<() => void>();

  // Create connect function without problematic dependencies
  connectRef.current = () => {
    if (!executionId || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/api/tests/ws/${executionId}`;
      
      console.log('Connecting to WebSocket:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected for execution:', executionId);
        setWsState(prev => ({
          ...prev,
          connected: true,
          error: null,
          reconnectAttempts: 0
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'progress':
              onProgressUpdate?.(data.payload as TestProgressUpdate);
              break;
              
            case 'apiCall':
              onApiCall?.(data.payload as APICallLog);
              break;
              
            case 'complete':
              onComplete?.(data.payload);
              break;
              
            case 'error':
              onError?.(data.payload.message || 'Test execution error');
              break;
              
            default:
              console.log('Unknown WebSocket message type:', data.type);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsState(prev => ({
          ...prev,
          connected: false,
          error: 'WebSocket connection error'
        }));
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setWsState(prev => ({
          ...prev,
          connected: false
        }));

        // Attempt to reconnect if not a clean close and we haven't exceeded max attempts
        if (event.code !== 1000) {
          setWsState(prev => {
            if (prev.reconnectAttempts < maxReconnectAttempts) {
              const newAttempts = prev.reconnectAttempts + 1;
              
              reconnectTimeoutRef.current = setTimeout(() => {
                console.log(`Attempting to reconnect (${newAttempts}/${maxReconnectAttempts})...`);
                connectRef.current?.();
              }, reconnectDelay);
              
              return {
                ...prev,
                reconnectAttempts: newAttempts
              };
            }
            return prev;
          });
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setWsState(prev => ({
        ...prev,
        connected: false,
        error: 'Failed to create WebSocket connection'
      }));
    }
  };

  const connect = useCallback(() => {
    connectRef.current?.();
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Component unmounting');
      wsRef.current = null;
    }

    setWsState({
      connected: false,
      error: null,
      reconnectAttempts: 0
    });
  }, []);

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected, cannot send message');
    }
  }, []);

  // Connect when executionId is provided
  useEffect(() => {
    if (executionId) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [executionId]); // Removed connect/disconnect from dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []); // Empty dependency array for cleanup

  return {
    connected: wsState.connected,
    error: wsState.error,
    reconnectAttempts: wsState.reconnectAttempts,
    sendMessage,
    disconnect
  };
};